import React, { useEffect, useState } from "react";
import { baseurl } from "../../Baseurl";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Autocomplete, TextField } from "@mui/material";
import axiosInstance from "../../api/axiosInstance";

// ─── Theme (matches Pharmacy module) ─────────────────────────────────────────
const C = { gray: '#5B6B79', blue: '#96C5F7', red: '#9E2B25', orange: '#F29E4C', green: '#2CA87F', purple: '#7c3aed' };
const inputStyle = { width: '100%', height: 34, border: '1.5px solid #dde3eb', borderRadius: 8, padding: '0 10px', fontSize: 13, color: '#1a202c', outline: 'none', boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, fontWeight: 600, color: C.gray, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };

// maxLength kept in sync with the rx_list column widths on the backend
const FIELDS = [
  { label: "Medicine Name", name: "medicine_name", maxLength: 255, full: true, icon: "ti-pill" },
  { label: "Strength", name: "strength", maxLength: 150, icon: "ti-gauge" },
  { label: "Unit", name: "unit", maxLength: 100, icon: "ti-ruler-2" },
  { label: "Pharmaceutical Form", name: "pharmaceutical_form", maxLength: 150, icon: "ti-tablet" },
  { label: "Frequency", name: "frequency", maxLength: 150, icon: "ti-repeat" },
  { label: "Duration", name: "duration", maxLength: 150, icon: "ti-calendar-time" },
  { label: "Dose", name: "dose", maxLength: 150, icon: "ti-droplet" },
  { label: "Route", name: "route", maxLength: 150, icon: "ti-route" },
  { label: "Product Type", name: "product_type", maxLength: 150, icon: "ti-category" },
  { label: "Pack Size", name: "packet_size", maxLength: 150, icon: "ti-package" },
];

const LabelIcon = ({ icon }) => (
  <i className={`ti ${icon}`} style={{ fontSize: 12, color: C.purple, marginRight: 4, verticalAlign: -1 }} />
);

export default function AddRxList() {
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [selectedWarnings, setSelectedWarnings] = useState([]);

  const [formData, setFormData] = useState({
    medicine_name: "",
    strength: "",
    unit: "",
    pharmaceutical_form: "",
    frequency: "",
    duration: "",
    notes: "",
    route: "",
    product_type: "",
    active_substances: "",
    packet_size: "",
    dose: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const requiredFields = [
      "medicine_name",
      "strength",
      "unit",
      "pharmaceutical_form",
      "frequency",
      "duration",
      "notes",
      "route",
      "product_type",
      "active_substances",
      "packet_size",
      "dose",
    ];

    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = `${field.replace(/_/g, " ")} is required`;
      }
    });

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        // Send only IDs for selected allergies as per API contract
        allergies: selectedWarnings
          .map((opt) => opt?.id ?? opt?.allergyId ?? opt?.value)
          .filter((v) => v !== undefined && v !== null),
      };

      const response = await axiosInstance.post(`addRxMedicine`, payload);
      if (response.data.success) {
        Swal.fire("Success", "RX Item added successfully!", "success");
        navigate("/Admin/Pharmacy");
      } else {
        Swal.fire("Error", response.data.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error(error);
      const serverMessage = error?.response?.data?.message;
      Swal.fire("Error", serverMessage || "Server error while adding RX item", "error");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setApiError(false);

    axiosInstance
      .get(`getAllAllergies`)
      .then((res) => {
        if (res.data.success && Array.isArray(res.data.data)) {
          setAllergies(res.data.data);
        } else {
          setApiError(true);
        }
        setLoading(false);
      });
  }, []);

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader" style={{ padding: '10px 16px' }}>
              <h5 style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0, fontSize: 15 }}>
                <span
                  style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.3rem' }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  &larr;
                </span>
                Add Rx Medicine
              </h5>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '14px 16px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {FIELDS.map((field) => (
                  <div key={field.name} style={{ flex: field.full ? '1 1 100%' : '1 1 200px' }}>
                    <label style={labelStyle}><LabelIcon icon={field.icon} />{field.label}<span style={{ color: C.red }}> *</span></label>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      maxLength={field.maxLength}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = C.purple)}
                      onBlur={(e) => (e.target.style.borderColor = '#dde3eb')}
                    />
                    {errors[field.name] && (
                      <div style={{ color: C.red, fontSize: 11, marginTop: 2 }}>{errors[field.name]}</div>
                    )}
                  </div>
                ))}

                <div style={{ flex: '1 1 100%' }}>
                  <label style={labelStyle}><LabelIcon icon="ti-flask" />Active Substances<span style={{ color: C.red }}> *</span></label>
                  <textarea
                    name="active_substances"
                    value={formData.active_substances}
                    onChange={handleChange}
                    rows={2}
                    style={{ ...inputStyle, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
                    onFocus={(e) => (e.target.style.borderColor = C.purple)}
                    onBlur={(e) => (e.target.style.borderColor = '#dde3eb')}
                  />
                  {errors.active_substances && (
                    <div style={{ color: C.red, fontSize: 11, marginTop: 2 }}>{errors.active_substances}</div>
                  )}
                </div>

                <div style={{ flex: '1 1 100%' }}>
                  <label style={labelStyle}><LabelIcon icon="ti-notes" />Notes<span style={{ color: C.red }}> *</span></label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={2}
                    style={{ ...inputStyle, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
                    onFocus={(e) => (e.target.style.borderColor = C.purple)}
                    onBlur={(e) => (e.target.style.borderColor = '#dde3eb')}
                  />
                  {errors.notes && (
                    <div style={{ color: C.red, fontSize: 11, marginTop: 2 }}>{errors.notes}</div>
                  )}
                </div>

                <div style={{ flex: '1 1 100%' }}>
                  <label style={labelStyle}><LabelIcon icon="ti-alert-triangle" />Warnings</label>
                  <Autocomplete
                    multiple
                    size="small"
                    id="warnings-autocomplete"
                    options={allergies}
                    disablePortal
                    getOptionLabel={(option) => {
                      return (
                        option?.warningName ||
                        option?.allergyName ||
                        option?.name ||
                        option?.label ||
                        ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) => {
                      const optionId = option?.id ?? option?.allergyId ?? option?.value;
                      const valueId = value?.id ?? value?.allergyId ?? value?.value;
                      return optionId === valueId;
                    }}
                    value={selectedWarnings}
                    onChange={(_, newValue) => setSelectedWarnings(newValue || [])}
                    loading={loading}
                    disabled={loading || apiError}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Search and select allergies..."
                        error={!!errors?.allergies}
                        helperText={errors?.allergies}
                      />
                    )}
                  />
                  {apiError && (
                    <div style={{ color: C.red, fontSize: 11, marginTop: 2 }}>Failed to load allergies list.</div>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
                <button type="submit" className="bgBtn btn-sm" disabled={submitting} style={{ padding: '7px 18px', fontSize: 13 }}>
                  {submitting ? "Adding..." : "Add RX Item"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
